import type { CadNode, FileInfo } from '@puppycad/proto'
import { CadFileKind } from '@puppycad/proto'
import type { ModelAssetFile, ModelAssetNode, ModelAssetNodeType, ModelAssetRole } from '@puppycad/cad-core'
import { type PartNodePackageMode, resolveCadNodeLoadStrategy } from './cad-node-load-strategy'
import { buildVersionFileUrl } from './version-file-url-builder'

const ASSEMBLY_MANIFEST_FILE_ID = '_assembly.json'
const IDENTITY_TRANSFORM = [1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1] as const

export interface MapCadNodeToModelAssetNodeInput {
  /**
   * 后端返回的 CAD 节点快照。
   */
  readonly cadNode: CadNode
  /**
   * 后端服务根地址，用于生成每个版本文件下载 URL。
   */
  readonly backendBaseUrl: string
  /**
   * 父节点稳定 key；根节点没有父节点。
   */
  readonly parentNodeKey?: string
  /**
   * 当前节点深度；OpenProject 根节点为 0。
   */
  readonly depth: number
  /**
   * 当前节点在兄弟节点中的序号，用于缺少实例身份时兜底生成稳定段。
   */
  readonly siblingIndex: number
  /**
   * 零件节点包选择模式；OpenProject/GetNodeTree 默认只认 `.puppy`，插入零部件可显式保留旧兼容模式。
   */
  readonly partPackageMode?: PartNodePackageMode
  /**
   * OpenProject/GetCadNodeTree 响应级总归档包；仅当前映射根节点消费。
   */
  readonly archiveFile?: PuppyCADWeb.CadArchiveFileSnapshot
}

/**
 * 将 proto 节点类型转换为 cad-core 可消费的项目模型节点类型。
 *
 * @param type 后端 CadFileKind。
 * @returns cad-core 节点类型。
 */
function mapNodeType(cadNode: CadNode): ModelAssetNodeType {
  if (cadNode.type === CadFileKind.PART) {
    return 'part'
  }

  if (cadNode.type === CadFileKind.ASSEMBLY) {
    return 'assembly'
  }

  throw new Error(
    `unsupported CadFileKind: repositoryId=${cadNode.repositoryId.toString()}, commitId=${cadNode.commitId}, type=${cadNode.type}`,
  )
}

/**
 * 判断文件是否是装配恢复清单。
 *
 * @param file 后端文件信息。
 * @returns 文件路径是否精确等于 `_assembly.json`。
 */
function isAssemblyManifestFile(file: FileInfo): boolean {
  return file.fileId === ASSEMBLY_MANIFEST_FILE_ID
}

/**
 * 判断后端文件是否是当前项目模型加载链路可直接解析的节点包。
 *
 * 设计说明：
 * 1. `CadNode.files` 可能混入预览 JSON、诊断文本或其它元数据，不能把所有非 `_assembly.json`
 *    文件都交给几何解析器；
 * 2. 当前 web-client 只支持 GLB 和 ZIP/Puppy 节点包，sidecar 必须由节点包自身携带；
 * 3. 在 mapper 边界过滤非几何文件，可以让错误停在协议适配层，避免一路掉到 GLB magic 预检。
 *
 * @param file 后端文件信息。
 * @returns 文件是否可作为 `ModelAssetNode.packageFiles`。
 */
function isSupportedNodePackageFile(file: FileInfo): boolean {
  const normalizedFileId = file.fileId.trim().toLowerCase()
  const normalizedKind = file.kind.trim().toLowerCase()

  return normalizedFileId.endsWith('.glb')
    || normalizedFileId.endsWith('.puppy')
    || normalizedFileId.endsWith('.zip')
    || normalizedKind === '.glb'
    || normalizedKind === '.puppy'
    || normalizedKind === 'application/zip'
}

/**
 * 解析当前节点的 `_assembly.json` 文件。
 *
 * `_assembly.json` 是节点恢复元数据，必须按固定 fileId 精确识别；如果后端返回多个，
 * 说明节点资产快照不可信，不能在 web-client 侧用 `find()` 静默折叠。
 *
 * @param cadNode 后端 CAD 节点。
 * @param nodeType 当前节点类型。
 * @returns 装配节点的恢复清单文件；没有时返回 undefined。
 */
function resolveAssemblyManifestFile(cadNode: CadNode, nodeType: ModelAssetNodeType): FileInfo | undefined {
  const manifestFiles = cadNode.files.filter(isAssemblyManifestFile)

  if (manifestFiles.length > 1) {
    throw new Error(
      `multiple _assembly.json files: repositoryId=${cadNode.repositoryId.toString()}, commitId=${cadNode.commitId}`,
    )
  }
  if (nodeType === 'part' && manifestFiles.length > 0) {
    throw new Error(
      `part node cannot contain _assembly.json: repositoryId=${cadNode.repositoryId.toString()}, commitId=${cadNode.commitId}`,
    )
  }

  return nodeType === 'assembly' ? manifestFiles[0] : undefined
}

/**
 * 转换文件描述，并将下载职责固定到版本文件接口。
 *
 * @param input 当前节点映射输入。
 * @param file 后端文件信息。
 * @param role 文件在模型加载中的职责。
 * @returns cad-core 可消费的资产文件描述。
 */
function mapFile(
  input: MapCadNodeToModelAssetNodeInput,
  file: FileInfo,
  role: ModelAssetRole,
  loadMode?: ModelAssetFile['loadMode'],
  source?: PuppyCADWeb.CadArchiveFileSnapshot,
): ModelAssetFile {
  // 完整归档可能存放在独立仓库和提交中，下载身份必须以 archiveFile 为准。
  const repositoryId = source?.repositoryId.toString() ?? input.cadNode.repositoryId.toString()
  const commitId = source?.commitId ?? input.cadNode.commitId

  return {
    fileId: file.fileId,
    uuid: file.uuid,
    kind: file.kind,
    size: file.size,
    url: buildVersionFileUrl({
      backendBaseUrl: input.backendBaseUrl,
      repositoryId,
      commitId,
      fileId: file.fileId,
    }),
    role,
    ...(loadMode ? { loadMode } : {}),
    ...(loadMode === 'complete-glb-scene'
      ? { completeGlbRootInstanceId: input.cadNode.instanceId.trim() }
      : {}),
  }
}

/**
 * 计算节点的稳定结构身份段。
 *
 * 当前 proto 尚未提供跨版本稳定的结构节点 ID，因此不能直接使用业务 occurrence 的
 * instance_id 作为 nodeKey。这里以实例名和兄弟序号组成结构路径；instance_id 只负责
 * 关联项目节点与 complete GLB 中的原始 glTF node。
 *
 * @param cadNode 后端 CAD 节点。
 * @param siblingIndex 兄弟节点序号兜底值。
 * @returns 当前节点在父节点下的稳定 key 片段。
 */
function buildNodeKeySegment(cadNode: CadNode, siblingIndex: number): string {
  const instanceName = cadNode.instanceName.trim()
  const segmentSource = instanceName || cadNode.repositoryId.toString()
  const segment = `${segmentSource}#${siblingIndex}`

  return encodeURIComponent(segment)
}

/**
 * 计算完整节点 key。
 *
 * @param cadNode 后端 CAD 节点。
 * @param parentNodeKey 父节点 key。
 * @param siblingIndex 兄弟节点序号。
 * @returns 当前节点完整稳定 key。
 */
function buildNodeKey(cadNode: CadNode, parentNodeKey: string | undefined, siblingIndex: number): string {
  const currentSegment = buildNodeKeySegment(cadNode, siblingIndex)

  return parentNodeKey ? `${parentNodeKey}/${currentSegment}` : currentSegment
}

/**
 * 复制后端局部矩阵；长度异常时使用单位矩阵，避免污染 cad-core 的加载契约。
 *
 * @param transform 后端返回的 4x4 展平矩阵。
 * @returns 长度为 16 的局部变换矩阵。
 */
function copyLocalTransform(transform: readonly number[]): readonly number[] {
  return transform.length === 16 ? [...transform] : [...IDENTITY_TRANSFORM]
}

/**
 * 将 OpenProject/GetCadNodeTree 返回的 CadNode 递归映射为 cad-core 的 ModelAssetNode。
 *
 * 命中响应级总归档或装配离线包时，本层只把对应权威包透传给导入链路，
 * 并在协议适配层清空 children，避免后续加载计划直接消费旧 children 子树。
 * `.puppy + _assembly.json` 的后端树扩展判断由公共策略负责，不能在 mapper 或 preparer 中各写一套。
 *
 * @param input 节点映射上下文。
 * @returns cad-core 可消费的模型资产节点。
 */
export function mapCadNodeToModelAssetNode(input: MapCadNodeToModelAssetNodeInput): ModelAssetNode {
  const nodeType = mapNodeType(input.cadNode)
  const nodeKey = buildNodeKey(input.cadNode, input.parentNodeKey, input.siblingIndex)
  const manifestFile = resolveAssemblyManifestFile(input.cadNode, nodeType)
  const loadStrategy = resolveCadNodeLoadStrategy(
    input.cadNode,
    nodeType,
    {
      ...(input.partPackageMode ? { partPackageMode: input.partPackageMode } : {}),
      ...(input.archiveFile ? { archiveFile: input.archiveFile } : {}),
    },
  )
  const packageFiles = loadStrategy.kind === 'offline-package'
    || loadStrategy.kind === 'complete-glb-scene'
    ? [mapFile(
        input,
        loadStrategy.packageFile,
        'node-package',
        loadStrategy.kind === 'complete-glb-scene' ? 'complete-glb-scene' : undefined,
        loadStrategy.kind === 'complete-glb-scene' ? loadStrategy.archiveFile : undefined,
      )]
    : loadStrategy.kind === 'empty-leaf'
      ? []
      : input.cadNode.files
        .filter((file) => !isAssemblyManifestFile(file) && isSupportedNodePackageFile(file))
        .map((file) => mapFile(input, file, 'node-package'))
  const children = loadStrategy.kind !== 'children-tree'
    ? []
    : input.cadNode.children.map((child, childIndex) =>
        mapCadNodeToModelAssetNode({
          cadNode: child,
          backendBaseUrl: input.backendBaseUrl,
          parentNodeKey: nodeKey,
          depth: input.depth + 1,
          siblingIndex: childIndex,
          ...(input.partPackageMode ? { partPackageMode: input.partPackageMode } : {}),
        }),
      )

  return {
    nodeKey,
    displayName: input.cadNode.instanceName.trim() || input.cadNode.repositoryId.toString(),
    // ModelAssetNode 暂时沿用历史属性名；初始映射时 occurrence 与包根都来自 CadNode.instance_id，
    // 版本切换会保留父装配 occurrence，而 complete GLB 根选择身份已经单独写入文件加载描述。
    instanceUuid: input.cadNode.instanceId.trim(),
    parentNodeKey: input.parentNodeKey,
    depth: input.depth,
    repositoryId: input.cadNode.repositoryId.toString(),
    branchId: input.cadNode.branchId.toString(),
    commitId: input.cadNode.commitId,
    versionKey: `${input.cadNode.repositoryId.toString()}:${input.cadNode.commitId}`,
    versionSwitchable: input.depth === 1,
    nodeType,
    // complete 包内 GLB 根已经携带完整场景矩阵；这里必须从单位矩阵进入项目组合，
    // 避免 CadNode 根 transform 与 GLB 根 transform 被重复应用。实例槽位位姿由上层挂载逻辑恢复。
    localTransform: loadStrategy.kind === 'complete-glb-scene'
      ? [...IDENTITY_TRANSFORM]
      : copyLocalTransform(input.cadNode.transform),
    // CadNode 协议不再返回包围盒，cad-core 资产字段先保持空数组，后续由几何加载链路计算真实范围。
    boundingBox: [],
    assemblyManifest: manifestFile ? mapFile(input, manifestFile, 'assembly-manifest') : undefined,
    packageFiles,
    children,
  }
}
